import axios from 'axios';
import { CartLineItem } from 'domains/cartDomain';
import {
  AdjustMarkupPayload,
  AdjustPressPayload,
  ArtworkFileParams,
  ChangeOrgPayload,
  JobConfigFileItem,
  JobEstimator,
  JobEstimatorComparatorItem,
  JobEstimatorConfirmPaperDTO,
  JobEstimatorConfirmPaperUpdateParams,
  JobRFQAdjustPayload,
  JobRFQOrgItemViewer,
  JobRFQResponseViewer,
  JobRFQSupplierResponseViewer,
  JobRFQViewer,
  OptionData,
  RFQSupplierResponseRequestDTO,
} from 'domains/jobconfigDomain';
import { Organization } from 'domains/orgDomain';
import { RFQListFilterParams } from 'domains/workspaceDomain';
import api from 'utils/api';
import { buildDocumentUrl } from 'utils/api';
import { getEffectiveUnit } from 'utils/measurement';
import { MeasurementUnitEnum } from 'domains/measurementDomain';

const jobEstimatorService = {
  changeOrg: (payload: ChangeOrgPayload) => {
    const {
      jobConfigId,
      orgId,
      quantityItemSerialNumber,
      comparatorItemSerialNumber,
    } = payload;
    return api.put<JobEstimatorComparatorItem>(
      `/job-config/${jobConfigId}/job-estimator/change-org?orgId=${orgId}&quantityItemSerialNumber=${quantityItemSerialNumber}&comparatorItemSerialNumber=${comparatorItemSerialNumber}`
    );
  },
  adjustPress: (payload: AdjustPressPayload) => {
    const {
      jobConfigId,
      quantityItemSerialNumber,
      comparatorItemSerialNumber,
      pressAdjusts,
    } = payload;
    return api.put<JobEstimatorComparatorItem>(
      `/job-config/${jobConfigId}/job-estimator/adjust-press?quantityItemSerialNumber=${quantityItemSerialNumber}&comparatorItemSerialNumber=${comparatorItemSerialNumber}`,
      {
        pressAdjusts,
      }
    );
  },
  adjustMarkup: (payload: AdjustMarkupPayload) => {
    const {
      jobConfigId,
      quantityItemSerialNumber,
      comparatorItemSerialNumber,
    } = payload;
    return api.put<JobEstimatorComparatorItem>(
      `/job-config/${jobConfigId}/job-estimator/adjust-markup?quantityItemSerialNumber=${quantityItemSerialNumber}&comparatorItemSerialNumber=${comparatorItemSerialNumber}`,
      {
        markupAdjust: payload,
      }
    );
  },
  adjustParcel: (payload: AdjustMarkupPayload) => {
    const {
      jobConfigId,
      quantityItemSerialNumber,
      comparatorItemSerialNumber,
    } = payload;
    return api.put<JobEstimatorComparatorItem>(
      `/job-config/${jobConfigId}/job-estimator/adjust-parcel?quantityItemSerialNumber=${quantityItemSerialNumber}&comparatorItemSerialNumber=${comparatorItemSerialNumber}`,
      {
        parcelCost: payload.parcelCost,
      }
    );
  },
  changeShipping: (payload: Partial<JobRFQAdjustPayload>) => {
    const { id, comparatorItemSerialNumber, qtySerialNumber, body } = payload;
    return api.put<JobEstimatorComparatorItem>(
      `/job-config/${id}/job-estimator/adjust-shipping?quantityItemSerialNumber=${qtySerialNumber}&comparatorItemSerialNumber=${comparatorItemSerialNumber}`,
      body
    );
  },
  getJobEstimator: (jobConfigId: string) => {
    return api.get<JobEstimator>(`/job-config/${jobConfigId}/job-estimator`);
  },
  generate: (payload: {
    jobConfigId: string;
    quantityItemSerialNumber: number;
  }) => {
    const { jobConfigId } = payload;
    return api.get<JobEstimator>(`/job-config/${jobConfigId}/job-estimator`);
  },
  viewQuotePdf: ({
    jobConfigId,
    quantityItemSerialNumber,
    comparatorItemSerialNumber,
    // The comparison step is inside the job wizard, which has its own unit selector - so a job
    // configured in millimetres prints in millimetres rather than in the reader's own setting.
    measurementUnit = getEffectiveUnit(),
  }: {
    jobConfigId: string;
    quantityItemSerialNumber: number;
    comparatorItemSerialNumber: number;
    measurementUnit?: MeasurementUnitEnum;
  }) => {
    return buildDocumentUrl(
      `/job-config/${jobConfigId}/job-estimator/quote-view`,
      {
        quantityItemSerialNumber,
        comparatorItemSerialNumber,
        // The quote prints sizes and weights, so it has to be told which unit to print them in.
        measurementUnit,
      }
    );
  },
  viewPOPdf: ({
    jobConfigId,
    quantityItemSerialNumber,
    comparatorItemSerialNumber,
    // The comparison step is inside the job wizard, which has its own unit selector - so a job
    // configured in millimetres prints in millimetres rather than in the reader's own setting.
    measurementUnit = getEffectiveUnit(),
  }: {
    jobConfigId: string;
    quantityItemSerialNumber: number;
    comparatorItemSerialNumber: number;
    measurementUnit?: MeasurementUnitEnum;
  }) => {
    return buildDocumentUrl(
      `/job-config/${jobConfigId}/job-estimator/po-view`,
      {
        quantityItemSerialNumber,
        comparatorItemSerialNumber,
        // The PO prints sizes and weights, so it has to be told which unit to print them in.
        measurementUnit,
      }
    );
  },
  viewValidatePdf: async ({
    jobConfigId,
    quantityItemSerialNumber,
    comparatorItemSerialNumber,
  }: {
    jobConfigId: string;
    quantityItemSerialNumber: number;
    comparatorItemSerialNumber: number;
  }) => {
    const authToken = localStorage.getItem('Authorization') ?? '';
    const url = `${process.env.REACT_APP_API_URL}/job-config/${jobConfigId}/job-estimator/validate?quantityItemSerialNumber=${quantityItemSerialNumber}&comparatorItemSerialNumber=${comparatorItemSerialNumber}`;
    const res = await axios.get(url, {
      responseType: 'blob',
      headers: {
        'Access-Control-Allow-Credentials': true,
        'Access-Control-Allow-Origin': '*',
        Accept: '*/*',
        Authorization: authToken,
      },
    });
    if (!(res.data instanceof Blob)) return;
    const blob = new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `estimator_validate.xlsx`;
    link.click();
  },
  addToCart: ({
    jobConfigId,
    quantityItemSerialNumber,
    comparatorItemSerialNumber,
  }: {
    jobConfigId: string;
    quantityItemSerialNumber: number;
    comparatorItemSerialNumber: number;
  }) => {
    return api.get<CartLineItem>(
      `/job-config/${jobConfigId}/job-estimator/add-to-cart?quantityItemSerialNumber=${quantityItemSerialNumber}&comparatorItemSerialNumber=${comparatorItemSerialNumber}`
    );
  },
  findAvlOrgs: async (payload: {
    jobConfigId: string;
    quantityItemSerialNumber: number;
  }) => {
    return await api.get(
      `/job-config/${payload.jobConfigId}/job-estimator/avl-orgs?comparatorItemSerialNumber=1&quantityItemSerialNumber=${payload.quantityItemSerialNumber}`
    );
  },
  init: async (payload: { jobConfigId: string; isProxied: boolean }) => {
    const { jobConfigId, isProxied } = payload;
    return await api.post<JobRFQViewer>(
      `/job-config/job-rfq/init?jobConfigId=${jobConfigId}&isProxied=${isProxied}`
    );
  },
  getRfqSupplierList: async () => {
    return await api.get<JobRFQOrgItemViewer[]>(
      `/job-config/job-rfq/supplier-list`
    );
  },
  getRfqSupplierResponseList: async (payload: RFQSupplierResponseRequestDTO) => {
    return await api.post<JobRFQSupplierResponseViewer[]>(
      `/job-config/job-rfq/supplier-response-list`,
      payload
    );
  },
  getNetworkSupplier: async () => {
    return api.get<Organization[]>(
      `/api/job-config/job-rfq/network-supplier`
    );
  },
  exportSupplierResponse: async (payload: Omit<RFQSupplierResponseRequestDTO, 'page' | 'size'>) => {
    const authToken = localStorage.getItem('Authorization') ?? '';
    const url = `${process.env.REACT_APP_API_URL}/job-config/job-rfq/export-supplier-response`;
    const res = await axios.post(url, payload, {
      responseType: 'blob',
      headers: {
        'Access-Control-Allow-Credentials': true,
        'Access-Control-Allow-Origin': '*',
        Accept: '*/*',
        Authorization: authToken,
      },
    });
    if (!(res.data instanceof Blob)) return;
    const blob = new Blob([res.data], {
      type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    });
    const link = document.createElement('a');
    link.href = window.URL.createObjectURL(blob);
    link.download = `supplier_response.xlsx`;
    link.click();
  },
  markAsRead: async (id: number | string) => {
    return await api.post<boolean>(`/job-config/job-rfq/mark-as-read/${id}`);
  },
  rfqDecline: async (orgItemId: number) => {
    return await api.post<JobRFQOrgItemViewer[]>(
      `/job-config/job-rfq/decline/${orgItemId}`
    );
  },
  rfqView: async (orgItemId: number, filterLatestRevision: boolean = true) => {
    return await api.get<JobRFQResponseViewer>(
      `/job-config/job-rfq/view/${orgItemId}?filterLatestRevision=${filterLatestRevision}`
    );
  },
  rfqSend: async ({
    orgItemId,
    serialNumbers,
  }: {
    orgItemId: number;
    serialNumbers: number[];
  }) => {
    return await api.post(
      `/job-config/job-rfq/response/${orgItemId}?serialNumbers=${serialNumbers.join(
        ','
      )}`
    );
  },
    rfqResponseUpdate: async ({
    orgItemId,
    serialNumbers,
  }: {
    orgItemId: number;
    serialNumbers: number[];
  }) => {
    return await api.post(
      `/job-config/job-rfq/response-update/${orgItemId}?serialNumbers=${serialNumbers.join(
        ','
      )}`
    );
  },
  rfqAdjust: async (payload: Partial<JobRFQAdjustPayload>) => {
    const { id, orgId, qtySerialNumber, body } = payload;
    return await api.post<JobRFQResponseViewer>(
      `/job-config/job-rfq/${id}/supplier-adjust?orgId=${orgId}&qtySerialNumber=${qtySerialNumber}`,
      body
    );
  },
  rfqAdjustNote: async (payload: Partial<JobRFQAdjustPayload>) => {
    const { id, body } = payload;
    return await api.post<JobRFQResponseViewer>(
      `/job-config/job-rfq/${id}/supplier-adjust`,
      body
    );
  },
  getPdfLink: ({
    template,
    rfqId,
    serialNumber,
    orgId,
    addOrgId = true,
  }: {
    template: string;
    rfqId?: number;
    serialNumber?: number;
    orgId?: number;
    addOrgId?: boolean;
  }) => {
    return buildDocumentUrl(`/jobs/pdf/generate/${template}`, {
      rfqId,
      serialNumber: serialNumber || 0,
      orgId: addOrgId ? orgId : undefined,
      // The ticket prints sizes and weights, so it has to be told which unit to print them in.
      measurementUnit: getEffectiveUnit(),
    });
  },
  getRfqCache: async (id: number) => {
    return await api.get(`/job-config/job-rfq/${id}/cache`);
  },
  getRfqFilesList: async (rfqId: number) => {
    return await api.get(`/job-config/job-rfq/${rfqId}/files/list`);
  },
  uploadFile: async (payload: ArtworkFileParams) => {
    const { jobId } = payload;
    return await api.post<JobConfigFileItem[]>(
      `/job-config/job-rfq/${jobId}/files`,
      payload
    );
  },
  deleteFile: async (payload: { rfqId: number; id: string }) => {
    const { rfqId, id } = payload;
    return await api.delete<JobConfigFileItem[]>(
      `/job-config/job-rfq/${rfqId}/files/${id}`
    );
  },
  getRfqSupplierFilesList: async (rfqId: number) => {
    return await api.get(`/job-config/job-rfq/${rfqId}/supplier-files/list`);
  },
    getRFQFilterJobNumber: async (params: RFQListFilterParams) => {
    return api.get<OptionData[]>(
      '/job-config/job-rfq/filter-data/rfq-number',
      params
    );
  },
  uploadRfqSupplierFile: async (payload: ArtworkFileParams) => {
    const { jobId } = payload;
    return await api.post<JobConfigFileItem[]>(
      `/job-config/job-rfq/${jobId}/supplier-files`,
      payload
    );
  },
  deleteRfqSupplierFile: async (payload: { rfqId: number; id: string }) => {
    const { rfqId, id } = payload;
    return await api.delete<JobConfigFileItem[]>(
      `/job-config/job-rfq/${rfqId}/supplier-files/${id}`
    );
  },
  rfqSupplierAdjust: async (payload: {
    id: number;
    orgId: number;
    qtySerialNumber: number;
  }) => {
    const { id, orgId, qtySerialNumber } = payload;
    return await api.get(
      `/job-config/job-rfq/${id}/supplier-adjust/avl-orgs?orgId=${orgId}&qtySerialNumber=${qtySerialNumber}`
    );
  },
  createConfirmPaper: async (
    jobConfigId: string,
    quantityItemSerialNumber: number
  ) => {
    return api.post<JobEstimatorConfirmPaperDTO>(
      `/job-config/${jobConfigId}/job-estimator/confirm-paper?quantityItemSerialNumber=${quantityItemSerialNumber}`
    );
  },
  updateConfirmPaper: async (
    jobConfigId: string,
    payload: JobEstimatorConfirmPaperUpdateParams
  ) => {
    return api.put<JobEstimatorConfirmPaperDTO>(
      `/job-config/${jobConfigId}/job-estimator/confirm-paper?quantityItemSerialNumber=${payload.quantityItemSerialNumber}`,
      payload
    );
  },
};

export default jobEstimatorService;
